<template>
    <div v-if="mode === 'AVAILABLE'" :class="availabilityLabelClass">
        {{ availabilityLabel }}
    </div>
    <div v-else class="text-body-2">
        <span class="mx-1">
            {{ availability?.allocatedQuantity ?? 0 }}
        </span>
        <span class="mx-1 text-primary">
            {{ availability?.bookedQuantity ?? 0 }}
        </span>
        <span class="mx-1 text-success">
            {{ availabilityLabel || 0 }}
        </span>
    </div>
</template>

<script setup lang="ts">
    import type {ZSHAvailabilityResult} from '../../utils/zshAvailability'

    const {availability} = defineProps<{
        availability: ZSHAvailabilityResult | any
    }>()

    const availabilityLabel = computed(() => {
        if (!availability) {
            return null
        }

        let baseLabel = ''

        switch (availability?.status) {
            case 'AVAILABLE':
                baseLabel = availability.availableQuantity + (availability.canBeUndefined ? '*' : '')
                break
            case 'UNLIMITED':
                baseLabel = '∞'
                break
            case 'RELEASED':
                baseLabel = 'R'
                break
            case 'OVERBOOKING':
                baseLabel = availability.availableQuantity ? availability.availableQuantity + '+' : 'OR'
                break
            case 'BLOCKED':
                baseLabel = 'B'
                break
            case 'TERMINATED_ALLOTMENT':
                baseLabel = 'TA'
                break
            default:
                baseLabel = availability.terminateAllotment ? 'TA' : availability.blockout ? 'B' : ''
        }

        // Apply ZSH special room display format
        // COMMENTED OUT: Constrained display format showing (initial) value
        // if (availability.displayFormat === 'constrained' && availability.initialAvailability !== undefined) {
        //     // Show format: "current (initial)" for constrained special rooms
        //     const currentValue = availability.availableQuantity + (availability.canBeUndefined ? '*' : '')
        //     const initialValue = availability.initialAvailability + (availability.canBeUndefined ? '*' : '')
        //
        //     // Only show the constrained format if current is different from initial
        //     if (currentValue !== initialValue) {
        //         return `${currentValue} (${initialValue})`
        //     }
        // }

        return baseLabel
    })

    const availabilityLabelClass = computed(() => {
        if (!availability) {
            return null
        }

        // Add special styling for constrained availability
        let baseClass = ''

        switch (availability?.status) {
            case 'RELEASED':
            case 'OVERBOOKING':
                baseClass = availability.availableQuantity ? '' : 'text-error font-weight-medium'
                break
            case 'BLOCKED':
            case 'TERMINATED_ALLOTMENT':
                baseClass = 'text-error font-weight-medium'
                break
            default:
                baseClass =
                    !availability.terminateAllotment && !availability.blockout ? '' : 'text-error font-weight-medium'
        }

        // Add visual indicator for constrained availability
        if (availability.isConstrainedByBasic) {
            baseClass += ' text-warning'
        }

        return baseClass
    })

    const mode = inject('availabilityTableViewMode')
</script>
